import React from 'react';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { X } from 'lucide-react';
import dayjs from 'dayjs';

import 'src/utils/format-date-time';
import { Flex } from 'src/components/flex';
import type { TourSlotSuggestionOption } from 'src/services/tour-availability/types';

import styles from 'src/pages/landlord/leads/messages/components/input/ReplySuggestions.module.css';

type Props = {
    slots: TourSlotSuggestionOption[];
    timezone: string | null;
    onSelect: (slot: TourSlotSuggestionOption) => void;
    /** Mark the suggestion not helpful — a real dismissal, tracked for performance. */
    onNotHelpful: () => void;
    /** Close the suggestion for the session without dismissing it, so tracking stays clean. */
    onClose: () => void;
};

const formatSlot = (isoDateTime: string, timezone: string | null): string => {
    const slot = timezone ? dayjs(isoDateTime).tz(timezone) : dayjs(isoDateTime);
    return slot.format('ddd, MMM D, h:mm A');
};

const timezoneLabel = (isoDateTime: string, timezone: string | null): string =>
    timezone ? dayjs(isoDateTime).tz(timezone).format('z') : '';

/** Single-focus nudge offering tour slots that match the lead's stated availability. */
export const TourSlotSuggestions = ({
    slots,
    timezone,
    onSelect,
    onNotHelpful,
    onClose,
}: Props) => {
    if (slots.length === 0) {
        return null;
    }

    const tzLabel = timezoneLabel(slots[0].start_time, timezone);

    return (
        <div className={styles.bar} data-testid="tour-slot-suggestions">
            <Typography variant="subtitle2" className={styles.heading}>
                {tzLabel ? `Suggested tour times (${tzLabel}):` : 'Suggested tour times:'}
            </Typography>
            <Flex columnGap={1} flexWrap="wrap" alignItems="center">
                {slots.map((slot) => (
                    <button
                        key={slot.start_time}
                        type="button"
                        className={styles.chip}
                        data-testid={`tour-slot-${slot.start_time}`}
                        onClick={() => onSelect(slot)}
                    >
                        <Typography variant="body2" component="span">
                            {formatSlot(slot.start_time, timezone)}
                        </Typography>
                    </button>
                ))}
            </Flex>
            <Flex columnGap={0.5} alignItems="center" className={styles.dismissAtEnd}>
                <Tooltip title="Click if suggestion is not helpful">
                    <Button
                        size="small"
                        color="error"
                        className={styles.notHelpfulButton}
                        data-testid="dismiss-tour-slots"
                        onClick={onNotHelpful}
                    >
                        Not helpful
                    </Button>
                </Tooltip>
                <IconButton
                    size="small"
                    color="error"
                    data-testid="close-tour-slots"
                    onClick={onClose}
                >
                    <X size={18} />
                </IconButton>
            </Flex>
        </div>
    );
};

export default TourSlotSuggestions;
